/**
 * FE-side capability vocabulary. Deliberately NOT a copy of the full backend
 * registry — only the wire keys the dashboard actually consumes. The backend's
 * `CapabilityKey` enum (generated REST schema) stays the transport contract;
 * this list is the domain contract for guard call sites, so an unknown key at
 * a call site is a compile error while unknown keys in API responses are
 * tolerated by `CapabilitySet` (plain string set).
 */
export const KnownCapabilityValues = [
  "USER:READ",
  "USER:WRITE",
  "USER:IMPERSONATE",
  "TEAM:READ",
  "TEAM:WRITE",
  "LICENSE:READ",
  "LICENSE:WRITE",
  "CLUB:READ",
  "CLUB:WRITE",
  "CLUB:CREATE",
  "CLUB:DELETE",
  "COACH:READ",
  "COACH:WRITE",
  "LEAGUE:READ",
  "LEAGUE:WRITE",
  "LEAGUE:CREATE",
  "LEAGUE:DELETE",
  "TOURNAMENT:READ",
  "TOURNAMENT:WRITE",
  "TOURNAMENT:CREATE",
  "TOURNAMENT:DELETE",
  "TOURNAMENT_REGISTRATION:READ",
  "TOURNAMENT_REGISTRATION:WRITE",
  "MATCH:READ",
  "MATCH:WRITE",
  "STANDINGS:READ",
  "STANDINGS:WRITE",
  "ORG:READ",
  "ORG:WRITE",
  "ORG:CREATE",
  "ORG:DETACH",
  "ORG_MANAGER:READ",
  "ORG_MANAGER:CREATE",
  "ORG_MANAGER:WRITE",
  "ORG_MANAGER:DELETE",
  "ORG_OWNER:WRITE",
  "ORG_FEATURE_FLAG:WRITE",
  "PLATFORM_OWNER:WRITE",
  "PLATFORM_FEE:READ",
  "PLATFORM_FEE:WRITE",
  "LEAGUE_FEE:READ",
  "LEAGUE_FEE:WRITE",
  "PLATFORM_MANAGER:READ",
  "PLATFORM_MANAGER:CREATE",
  "PLATFORM_MANAGER:WRITE",
  "PLATFORM_MANAGER:DELETE",
  "PLATFORM_SETTINGS:READ",
  "PLATFORM_SETTINGS:WRITE",
  "ORG_FINANCE:READ",
  "PLATFORM_FINANCE:READ",
  "ORG_REPORT:READ",
  "RANKING:READ",
  "RANKING:WRITE",
  "CATEGORY:READ",
  "CATEGORY:WRITE",
  "PLAYER:READ",
  "LEAGUE_MANAGER:READ",
  "LEAGUE_MANAGER:CREATE",
  "LEAGUE_MANAGER:WRITE",
  "LEAGUE_MANAGER:DELETE",
  "LEAGUE_FINANCE:READ",
  "PLATFORM_AUDIT:READ",
  "ORG_AUDIT:READ",
  "LEAGUE_AUDIT:READ",
  "PLATFORM_NEWS:READ",
  "PLATFORM_NEWS:CREATE",
  "PLATFORM_NEWS:WRITE",
  "PLATFORM_NEWS:DELETE",
  "ORG_NEWS:READ",
  "ORG_NEWS:CREATE",
  "ORG_NEWS:WRITE",
  "ORG_NEWS:DELETE",
  "LEAGUE_NEWS:READ",
  "LEAGUE_NEWS:CREATE",
  "LEAGUE_NEWS:WRITE",
  "LEAGUE_NEWS:DELETE",
  "PLATFORM_GALLERY:CREATE",
  "PLATFORM_GALLERY:WRITE",
  "PLATFORM_GALLERY:DELETE",
  "ORG_GALLERY:CREATE",
  "ORG_GALLERY:WRITE",
  "ORG_GALLERY:DELETE",
  "LEAGUE_GALLERY:CREATE",
  "LEAGUE_GALLERY:WRITE",
  "LEAGUE_GALLERY:DELETE",
  "PLATFORM_PARTNER:READ",
  "PLATFORM_PARTNER:WRITE",
  "ORG_PARTNER:READ",
  "ORG_PARTNER:WRITE",
  "LEAGUE_PARTNER:READ",
  "LEAGUE_PARTNER:WRITE",
  "PLATFORM_BANNER:READ",
  "PLATFORM_BANNER:WRITE",
] as const;
export type KnownCapability = (typeof KnownCapabilityValues)[number];

/**
 * Opaque set of wire capability keys a user holds on one scope node. Keyed by
 * plain strings so keys the FE doesn't know about (newer vocabulary) pass
 * through without breaking; guards narrow via `KnownCapability` at call sites.
 */
export type CapabilitySet = ReadonlySet<string>;

/**
 * The three dashboard context levels a capability set can be resolved against
 * (platform shell, current organization, current league).
 */
export const ScopeLevelValues = ["platform", "organization", "league"] as const;
export type ScopeLevel = (typeof ScopeLevelValues)[number];

/** Shared empty set — stable reference for "no capabilities resolved". */
export const EMPTY_CAPABILITY_SET: CapabilitySet = new Set();
